import SEO from "@/components/SEO";
import { RevealBlock } from "@/components/Reveal";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

export default function About() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  return (
    <>
      <SEO
        title="About Nova Scotia Security Services"
        description="An editorial-grade security company based in Halifax, licensed under the Nova Scotia Security and Investigative Services Act, deployed across the province since 2007."
        path="/about"
      />

      <section ref={ref} className="relative overflow-hidden">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 pt-16 md:pt-20 pb-24">
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#C17A43] mb-8">
            — About
          </p>
          <RevealBlock>
            <h1 className="font-serif text-[clamp(3rem,9vw,9rem)] leading-[0.88] tracking-tighter text-[#111111]">
              Standing<br />
              <span className="italic text-[#184C59]">for the province.</span>
            </h1>
          </RevealBlock>

          <div className="grid grid-cols-12 gap-8 mt-16 md:mt-24">
            <div className="col-span-12 lg:col-span-6">
              <RevealBlock>
                <p className="font-serif italic text-3xl md:text-5xl leading-tight text-[#111111]">
                  Nova Scotia Security Services was founded in Halifax in 2007
                  with a single premise — a security company worth hiring should
                  read like a serious institution, not a directory ad.
                </p>
              </RevealBlock>
            </div>
            <div className="col-span-12 lg:col-span-6 lg:pt-16">
              <RevealBlock delay={0.1}>
                <p className="font-sans text-base md:text-lg leading-relaxed text-[#111111]/80 mb-6">
                  Eighteen years later, we hold contracts across mining sites in
                  Cape Breton and Windsor, cruise terminals in Halifax and Sydney,
                  construction megaprojects along the Bedford Highway corridor,
                  festival stages from Antigonish to Lunenburg, and private
                  estates from Chester to the Cabot Trail.
                </p>
                <p className="font-sans text-base md:text-lg leading-relaxed text-[#111111]/80 mb-6">
                  Our officers are hand-picked, licensed under the Nova Scotia
                  Security and Investigative Services Act, and trained in
                  de-escalation, first aid, and Nova Scotia-specific site
                  protocols before their first shift.
                </p>
                <p className="font-sans text-base md:text-lg leading-relaxed text-[#111111]/80">
                  We are bonded and insured to $5M CGL, WCB in good standing,
                  and audited annually.
                </p>
              </RevealBlock>
            </div>
          </div>

          <div className="relative mt-24 aspect-[21/9] overflow-hidden border border-[#D9D6D0]">
            <motion.img
              style={{ y }}
              src="https://images.unsplash.com/photo-1706555693525-fb90cbe18826?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxub3ZhJTIwc2NvdGlhJTIwY29hc3R8ZW58MHx8fHwxNzg1MTA1Njc4fDA&ixlib=rb-4.1.0&q=85"
              alt="Nova Scotia coast — the province we serve"
              className="absolute inset-0 w-full h-[130%] object-cover grayscale contrast-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#111111]/60 to-transparent" />
            <p className="absolute bottom-4 left-4 font-mono text-[10px] uppercase tracking-[0.28em] text-[#F5F3EF]">
              Fig. 07 · The province we serve · Peggy&apos;s Cove ↓ Cape North
            </p>
          </div>

          <div className="grid grid-cols-12 gap-8 mt-24">
            <div className="col-span-12 lg:col-span-3">
              <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#C17A43]">
                — Compliance
              </p>
            </div>
            <div className="col-span-12 lg:col-span-9">
              <ul className="border-t border-[#D9D6D0]">
                {[
                  "Nova Scotia Security & Investigative Services Act licence — active",
                  "$5,000,000 commercial general liability insurance",
                  "Workers' Compensation Board (WCB) — in good standing",
                  "Standard First Aid + CPR-C — required of every officer",
                  "Site-specific WHMIS orientation for industrial contracts",
                  "Health Canada physical security guidance for cannabis clients",
                  "Transport Canada MTSR / ISPS awareness for marine contracts",
                  "Non-violent crisis intervention — required for healthcare and event officers",
                ].map((c, i) => (
                  <li
                    key={c}
                    className="border-b border-[#D9D6D0] py-5 grid grid-cols-12 gap-4"
                  >
                    <span className="col-span-2 font-mono text-[10px] uppercase tracking-[0.28em] text-[#C17A43]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="col-span-10 font-serif text-lg md:text-xl text-[#111111]">
                      {c}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
